import { useEffect } from 'react';
import { useForm, Controller } from 'react-hook-form';
import { useDispatch } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { Password } from 'primereact/password';
import { Button } from 'primereact/button';
import { useLoginMutation } from '../../../api/authApi';
import { logout } from '../../../slices/authSlice';
import { useToast } from '../../../context/ToastContext';
import { errMsg } from '../../../utils/errMsg';
import getSubdomain from '../../../utils/getSubdomain';
import buttons from '../../../styles/shared/buttons.module.css';
import card from '../../../styles/shared/publicCard.module.css';
import styles from './LoginPage.module.css';

const HOME_BY_ROLE = {
  admin: '/admin/organizations',
  manager: '/manager/groups',
  host: '/host/giveaways',
};

export default function LoginPage() {
  const [login, { isLoading }] = useLoginMutation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const showToast = useToast();
  const subdomainSlug = getSubdomain();

  useEffect(() => {
    dispatch(logout());
  }, [dispatch]);

  const { control, handleSubmit, watch } = useForm({
    defaultValues: { email: '', password: '', organizationSlug: '' },
  });
  const password = watch('password');

  async function onSubmit(values) {
    try {
      const normalizedEmail = values.email.trim().toLowerCase();
      const normalizedPassword = values.password.trim();
      const normalizedOrganizationSlug = subdomainSlug || values.organizationSlug.trim();
      const body = { email: normalizedEmail, password: normalizedPassword };
      if (normalizedOrganizationSlug) body.organization_slug = normalizedOrganizationSlug;
      const { user } = await login(body).unwrap();
      const home = HOME_BY_ROLE[user.role] ?? '/';
      const from = location.state?.from?.pathname;
      const allowedFrom = from && from.startsWith(`/${user.role}`) ? from : undefined;
      navigate(allowedFrom ?? home, { replace: true });
    } catch (err) {
      showToast('error', 'Login', errMsg(err, 'Login failed.'));
    }
  }

  return (
    <div className={card.wrapper}>
      <Card title="Staff login" className={`${card.card} ${styles.card}`}>
        <form onSubmit={handleSubmit(onSubmit)} className="p-fluid">
          <Controller
            name="email"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className={`field ${styles.fieldContainer}`}>
                <label htmlFor={field.name}>Email</label>
                <InputText id={field.name} type="email" placeholder="you@campus.edu" {...field} />
              </div>
            )}
          />

          <Controller
            name="password"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <div className={`field ${styles.fieldContainer}`}>
                <label htmlFor={field.name}>Password</label>
                <Password id={field.name} toggleMask feedback={false} placeholder="Password" {...field} />
                {password !== password.trim() && (
                  <small className={styles.hint}>
                    Leading and trailing spaces in password are ignored.
                  </small>
                )}
              </div>
            )}
          />

          {!subdomainSlug && (
            <Controller
              name="organizationSlug"
              control={control}
              render={({ field }) => (
                <div className={`field ${styles.fieldContainer}`}>
                  <label htmlFor={field.name}>Organization slug (only needed without a subdomain)</label>
                  <InputText id={field.name} {...field} />
                </div>
              )}
            />
          )}

          <Button
            type="submit"
            label={isLoading ? 'Logging in…' : 'Log in'}
            loading={isLoading}
            className={`${buttons.accentBtn} ${styles.submitButton}`}
          />
        </form>
      </Card>
    </div>
  );
}
